import React, { useState, useEffect } from "react";
import { admin_login_analytics } from "../admin.api";
import Loader from "../../../components/loader/loader";
import moment from "moment";
import {
  AiOutlineLineChart,
  AiOutlineUser,
  AiOutlineClockCircle,
  AiOutlineCheckCircle,
  AiOutlineSearch,
  AiOutlineCalendar
} from "react-icons/ai";
import "./style.css";

export default function LoginAnalytics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD')
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await admin_login_analytics(dateRange);
      if (res.success) {
        setData(res.data);
      }
    } catch (error) {
      console.error("Error fetching login analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) return <Loader open={true} />;

  const summary = data?.summary || {};

  const AnalyticsCard = ({ label, value, icon, subValue }) => (
    <div className="col-md-3">
      <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px', background: '#fff' }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="p-2 rounded-3 bg-light text-secondary">
              {icon}
            </div>
            {subValue && <span className="badge bg-light text-muted fw-normal">{subValue}</span>}
          </div>
          <p className="text-muted small fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.5px' }}>{label}</p>
          <h3 className="fw-bold text-dark mb-0">{value}</h3>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4" style={{ minHeight: '100vh', background: '#f5f7f9' }}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 px-2">
        <div>
          <h4 className="fw-bold text-dark m-0">Login Insights</h4>
          <p className="text-muted small mb-0">Auditing and monitoring system-wide access metrics.</p>
        </div>
        <div className="d-flex align-items-center gap-2 bg-white p-2 rounded-3 shadow-sm border">
          <AiOutlineCalendar className="text-muted" />
          <input
            type="date"
            className="form-control form-control-sm border-0 bg-transparent"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            style={{ width: '130px', cursor: 'pointer' }}
          />
          <span className="text-muted">to</span>
          <input
            type="date"
            className="form-control form-control-sm border-0 bg-transparent"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            style={{ width: '130px', cursor: 'pointer' }}
          />
        </div>
      </div>

      {/* Summary Section */}
      <div className="row g-3 mb-4">
        <AnalyticsCard
          label="Total Logins"
          value={summary.total_logins || 0}
          icon={<AiOutlineLineChart size={20} />}
          subValue="All Users"
        />
        <AnalyticsCard
          label="Unique Users"
          value={summary.unique_users || 0}
          icon={<AiOutlineUser size={20} />}
        />
        <AnalyticsCard
          label="Success Rate"
          value={`${summary.success_rate || 0}%`}
          icon={<AiOutlineCheckCircle size={20} />}
          subValue="Authorization"
        />
        <AnalyticsCard
          label="Avg. Session"
          value={`${summary.avg_session_duration_minutes || 0}m`}
          icon={<AiOutlineClockCircle size={20} />}
        />
      </div>

      <div className="row g-4">
        {/* Main Visualization */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold m-0 text-white">Activity Distribution (Peak Hours)</h6>
              <span className="text-muted small">Hourly granularity</span>
            </div>
            <div className="card-body p-4">
              <div className="d-flex align-items-end gap-1" style={{ height: '280px' }}>
                {(data?.peak_hours || []).map((item, i) => {
                  const maxCount = Math.max(...data.peak_hours.map(h => h.count)) || 1;
                  const heightPercentage = (item.count / maxCount) * 100;
                  return (
                    <div key={i} className="flex-grow-1 d-flex flex-column align-items-center gap-2 group">
                      <div className="small text-muted opacity-0 group-hover:opacity-100 transition-opacity" style={{ fontSize: '9px' }}>{item.count}</div>
                      <div className="w-100" style={{
                        height: `${heightPercentage}%`,
                        background: i % 2 === 0 ? '#495057' : '#ced4da',
                        borderRadius: '3px 3px 0 0',
                        minHeight: '2px',
                        transition: 'all 0.3s ease'
                      }}></div>
                      <div className="small text-muted" style={{ fontSize: '9px', fontWeight: '500' }}>{item.hour}h</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 py-3 px-4">
              <h6 className="fw-bold m-0 text-white">Chronological Login Logs</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="px-4 py-3 border-0 small text-uppercase text-muted" style={{ letterSpacing: '0.5px' }}>Date</th>
                      <th className="py-3 border-0 small text-uppercase text-muted" style={{ letterSpacing: '0.5px' }}>Session Count</th>
                      <th className="px-4 py-3 border-0 small text-uppercase text-muted text-end" style={{ letterSpacing: '0.5px' }}>Trend Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.daily_logins || []).slice(-10).reverse().map((item, i) => {
                      const max = Math.max(...(data?.daily_logins || []).map(d => d.count)) || 1;
                      return (
                        <tr key={i}>
                          <td className="px-4 py-3 fw-medium text-dark">{moment(item.date).format('MMM DD, YYYY')}</td>
                          <td className="py-3">
                            <span className="badge bg-light text-dark px-3 py-2 border">{item.count}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="d-flex align-items-center justify-content-end gap-2">
                              <div className="progress flex-grow-1" style={{ height: '4px', maxWidth: '100px', background: '#e9ecef' }}>
                                <div className="progress-bar" style={{ width: `${(item.count / max) * 100}%`, background: '#343a40' }}></div>
                              </div>
                              <span className="small text-muted" style={{ minWidth: '35px' }}>{Math.round((item.count / max) * 100)}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Side panels */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 py-3 px-4">
              <h6 className="fw-bold m-0 text-dark">Platform Distribution</h6>
            </div>
            <div className="card-body p-4">
              {(data?.logins_by_device || []).map((item, i) => (
                <div key={i} className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="small fw-bold text-muted text-uppercase" style={{ letterSpacing: '0.5px' }}>{item.device_type}</span>
                    <span className="small fw-bold text-dark">{item.count}</span>
                  </div>
                  <div className="progress" style={{ height: '6px', background: '#f1f3f5' }}>
                    <div className="progress-bar" style={{
                      width: `${(item.count / summary.total_logins) * 100}%`,
                      backgroundColor: i === 0 ? '#212529' : i === 1 ? '#6c757d' : '#adb5bd'
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
            <div className="card-header bg-white border-0 py-3 px-4">
              <h6 className="fw-bold m-0 text-white">Most Active Accounts</h6>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {(data?.top_users || []).map((user, i) => (
                  <div key={i} className="list-group-item p-3 border-0 border-bottom d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-light d-flex align-items-center justify-content-center fw-bold text-dark border" style={{ width: '42px', height: '42px', fontSize: '14px' }}>
                      {user.name.charAt(0)}
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-bold text-dark text-truncate small">{user.name}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: '11px' }}>{user.email}</div>
                    </div>
                    <div className="fw-bold text-dark px-2 py-1 bg-light border rounded small">
                      {user.login_count}
                    </div>
                  </div>
                ))}
              </div>
              {/* <div className="p-3 text-center">
                <button className="btn btn-link text-muted text-decoration-none small fw-bold" onClick={() => window.location.href = '/admin/login-logs'}>
                  View Detailed Logs <AiOutlineSearch />
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
